import type { ReactNode } from "react";
import { cn } from "@shared/lib/utils";
import { StatPair, type StatPairProps } from "./StatPair";

/**
 * §1.1 page header — fixed 3 layers: mono 12px uppercase eyebrow →
 * Inter 700 title (40–48px, letter-spacing normal, noun phrase) →
 * one row of 1–3 key figures (label + tnum value pairs).
 */
export function PageHeader({
  eyebrow,
  title,
  stats,
  children,
  className,
  titleClassName,
}: {
  eyebrow: string;
  title: string;
  /** 1–3 judgment figures; extra entries are dropped (§1.1) */
  stats?: StatPairProps[];
  /** extra row content sharing the stat line, e.g. a Countdown */
  children?: ReactNode;
  className?: string;
  /** Opt-in title override — serif marketing headers only (scroll-asset-prd §3). */
  titleClassName?: string;
}) {
  return (
    <header className={cn("flex flex-col gap-3", className)}>
      <p className="ty-eyebrow text-xs">{eyebrow}</p>
      <h1
        className={cn(
          "font-display text-[40px] font-bold leading-[1.15] text-ink sm:text-5xl sm:leading-[56px]",
          titleClassName,
        )}
      >
        {title}
      </h1>
      {((stats?.length ?? 0) > 0 || children) && (
        <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
          {stats?.slice(0, 3).map((s) => <StatPair key={s.label} {...s} />)}
          {children}
        </div>
      )}
    </header>
  );
}
